import { ReactNode } from "react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Icon } from "@/components/ui/icon"

interface PageHeadingProps {
  title: string
  description?: string
  children?: ReactNode
  className?: string
  backButton?: ReactNode
  alignment?: "start" | "center"
  action?: {
    label: string
    onClick: () => void
    icon?: string
    variant?: "default" | "outline" | "secondary" | "ghost" | "link" | "destructive"
  }
}

export function PageHeading({ 
  title, 
  description, 
  children, 
  className,
  backButton,
  alignment = "start",
  action
}: PageHeadingProps) {
  const isCenter = alignment === "center"

  return (
    <div className={cn(
      "flex flex-col gap-6 mb-4",
      isCenter ? "items-center text-center" : "md:flex-row justify-between items-start md:items-end",
      className
    )}>
      <div className={cn("flex flex-col gap-2", isCenter && "items-center")}>
        {backButton ? (
          <div className={cn("w-fit", !isCenter && "self-start")}>{backButton}</div>
        ) : null}
        
        <h2 className="text-4xl lg:text-5xl font-black text-foreground tracking-tight">
          {title}
        </h2>
        
        {description && (
          <p className="text-lg text-muted-foreground font-medium max-w-2xl">
            {description}
          </p>
        )}
      </div>

      {(children || action) && (
        <div className="shrink-0 w-full md:w-auto">
          {action ? (
            <Button 
              onClick={action.onClick} 
              variant={action.variant || "default"}
              className="w-full md:w-auto h-14 px-8 rounded-xl text-base font-bold shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all hover:-translate-y-0.5 shrink-0"
            >
              {action.icon && <Icon name={action.icon} />}
              <span>{action.label}</span>
            </Button>
          ) : children}
        </div>
      )}
    </div>
  )
}
